/**
 * Mini status badges for a venue's application counts (task T.C4a).
 *
 * The vocabulary (order, tone, RU label, zero-normalisation) is the dashboard's
 * — the campaign cards above its table are the legend these bare counts point
 * back at. Only the RENDERING lives here: the venues table is compact (a venue
 * that has seen no rejections does not print `0 Отказ`), the card prints the
 * same badges with the same rule.
 */

import type { VenueStatusCountMap } from '../../api/venues';
import { Badge } from '../../components/ui';

import { MISSING } from '../dashboard/format';
import { DASHBOARD_STATUSES, normalizeCount, statusLabel, statusTone } from '../dashboard/status';

export function VenueStatusBadges({
  counts,
  testIdPrefix,
}: {
  counts: Partial<VenueStatusCountMap> | null | undefined;
  /** `data-testid` prefix; the status key is appended (`<prefix>-<STATUS>`). */
  testIdPrefix?: string;
}) {
  const visible = DASHBOARD_STATUSES.filter((status) => normalizeCount(counts?.[status]) > 0);

  if (visible.length === 0) {
    return (
      <span className="cell-muted" data-testid={testIdPrefix ? `${testIdPrefix}-none` : undefined}>
        {MISSING}
      </span>
    );
  }

  return (
    <div className="venue-badges">
      {visible.map((status) => {
        const count = normalizeCount(counts?.[status]);
        return (
          <span
            key={status}
            className="venue-badge"
            data-status={status}
            data-testid={testIdPrefix ? `${testIdPrefix}-${status}` : undefined}
          >
            <Badge tone={statusTone(status)} title={`${statusLabel(status)}: ${count}`}>
              {count}
            </Badge>
          </span>
        );
      })}
    </div>
  );
}
